import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { AddInlineEditTableComponent } from '../../../../../shared/components/tables/add-inline-edit-table/add-inline-edit-table.component';
import {
  accountUsersTableColumnConfig,
  accountUserServiceFormConfig,
} from '../../accounts.config';
import {
  ActionIcons,
  ModalPopupMessages,
  ModeIcons,
  popupWidth,
  selectionTableNames,
  SingleDeleteHeaderText,
} from '../../../../../shared/utility/constants';
import {
  AccountsUserFormPayload,
  AccountUser,
} from '../../../../model/sm-accounts';
import { Store } from '@ngxs/store';
import {
  combineLatestWith,
  filter,
  lastValueFrom,
  Observable,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SvgIconProp } from '../../../../../shared/models/dynamic.model';
import { CommonService } from '../../../../../shared/services/common.service';
import { AccountUserManagementDefaultHeaderIcons } from '../../../../../shared/utility/svg-constants';
import { AccountsService } from '../../../../service/accounts.service';
import { AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AddAccountUser,
  RemoveAccountUser,
  UpdateAccountUser,
} from '../../store/accounts.action';
import { ModalService } from '../../../../../shared/services/modal.service';
import { MessagePopupComponent } from '../../../../../shared/components/message-popup/message-popup.component';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly commonService = inject(CommonService);
  private readonly accountsService = inject(AccountsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);
  protected readonly accountUsersTableColumnConfig =
    accountUsersTableColumnConfig;
  protected readonly accountUserServiceFormConfig =
    accountUserServiceFormConfig;
  protected readonly tableName = selectionTableNames;
  @ViewChild('inlineTable', { static: true })
  inlineTable!: AddInlineEditTableComponent;
  users$: Observable<AccountUser[]> = this.store.select(
    state => state.accountUsers.users
  );
  public headerTextMap = SingleDeleteHeaderText;
  headerIcons: SvgIconProp[] = structuredClone<SvgIconProp[]>(
    AccountUserManagementDefaultHeaderIcons
  );
  pageMode!: string;
  tableState?: string;
  editingId?: number;
  iconClickHandlerMap = {
    [ActionIcons.save]: this.handleSaveClicked.bind(this),
    [ActionIcons.close]: this.handleCloseClicked.bind(this),
    [ActionIcons.mail]: this.handleResendMailClicked.bind(this),
    [ActionIcons.add]: this.handleAddClicked.bind(this),
    [ActionIcons.edit]: this.handleEditClicked.bind(this),
    [ActionIcons.delete]: this.handleDeleteClicked.bind(this),
  };
  fullNameListenerSubscription?: Subscription;

  get isIbcUsersEditable(): boolean {
    return this.pageMode !== ModeIcons.view || this.tableState === ModeIcons.add;
  }

  ngAfterViewInit() {
    this.checkMode();
    this.initializeUsersGrid();
    this.inlineTable
      .getRows()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.accountsService.checkAccountUserData(
          this.inlineTable.getRows().controls.every(control => {
            if (control.enabled && (control.valid || control.pristine)) {
              return true;
            }

            return !control.invalid;
          })
        );
        this.updateTableIcons();
      });
  }

  handleSaveClicked() {
    const row = this.getRowBeingEdited();
    row.updateValueAndValidity();
    row.markAsTouched();

    if (row.invalid) {
      return;
    }

    if (
      this.isAccountNotServiceable() &&
      (!row.value.id || this.tableState === ModeIcons.add)
    ) {
      this.showNotServiceableMessageAlert(
        ModalPopupMessages.nonServiceableAccountUserCreationErrorMessage
      );

      return;
    }

    const value = row.getRawValue();

    const mappedUser = {
      id: value.id || undefined,
      fullName: value.fullName,
      email: value.email,
      accountId: +this.activatedRoute.snapshot.queryParamMap.get('id')!,
      isCustomer: false,
      isExternal: true,
    } as AccountsUserFormPayload;

    if (this.pageMode === ModeIcons.view) {
      this.accountsService.createOrUpdateUsers(mappedUser).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
        row.patchValue(
          { ...data.data, isRowDisable: true },
          { emitEvent: false }
        );
        row.disable();
        this.inlineTable.inlineTableSelection.clear();
        this.resetStateAfterAPISuccess();
      });
      return;
    }

    // Add user update to a parent account object
    if (this.tableState === ModeIcons.add) {
      // get the topmost form row and save
      mappedUser.id = row.value.tempId;
      this.store.dispatch(
        new AddAccountUser(mappedUser as unknown as AccountUser)
      );
    } else if (this.tableState === ModeIcons.edit) {
      this.store.dispatch(
        new UpdateAccountUser(mappedUser as unknown as AccountUser)
      );
    }
    this.commonService.checkPristineAccordionData(false);
    row.disable();
    row.patchValue({ ...mappedUser, isRowDisable: true }, { emitEvent: false });
    this.fullNameListenerSubscription?.unsubscribe();
    this.handleAddClicked();
  }

  getRowBeingEdited(): AbstractControl {
    if (this.tableState === ModeIcons.add) {
      return this.inlineTable.getRows().at(0);
    }

    const index = this.inlineTable
      .getRows()
      .getRawValue()
      .findIndex(u => u.id === this.editingId);

    return this.inlineTable.getRows().at(index);
  }

  async handleCloseClicked() {
    if (this.tableState === ModeIcons.add) {
      if (this.pageMode !== ModeIcons.view) {
        this.inlineTable
          .getRows()
          .at(0)
          .reset({ firstName: '', lastName: '', email: '' });
        this.inlineTable.getRows().at(0).markAsPristine();
        return;
      }
      this.inlineTable.getRows().removeAt(0);
    } else if (this.tableState === ModeIcons.edit) {
      const formRow = this.getRowBeingEdited();
      const users = await lastValueFrom(this.users$.pipe(take(1)));
      const existingUser = users.find(user => formRow.value.id === user.id);
      formRow.patchValue(existingUser);
      formRow.disable();
    }

    this.resetStateAfterAPISuccess();
  }

  getSelectionIds() {
    return this.inlineTable.inlineTableSelection.selected
      .map(user => user.id)
      .filter(id => !!id && !id?.toString()?.includes('temp-'));
  }

  isAccountNotServiceable() {
    const accountDetail = this.store.selectSnapshot(
      state => state.accountsDetail
    );
    return (
      !accountDetail.serviceable && Object.hasOwn(accountDetail, 'serviceable')
    );
  }

  showNotServiceableMessageAlert(message: string) {
    this.modalService.openPopup(
      {
        message,
        isForWarning: true,
        isFooterRequired: true,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  handleResendMailClicked() {
    if (this.isAccountNotServiceable()) {
      this.showNotServiceableMessageAlert(
        ModalPopupMessages.nonServiceableAccountUserResendErrorMessage
      );

      return;
    }
    this.accountsService
      .resendEmailWithConfirmation(this.getSelectionIds())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  handleAddClicked() {
    this.tableState = ModeIcons.add;
    this.inlineTable.addNewRowAtTop();
    const row = this.getRowBeingEdited();
    row
      .get('tempId')
      ?.setValue(`temp-${this.commonService.generateUniqueTempId()}`, {
        emitEvent: false,
      });
    this.inlineTable.getTableData();
    this.updateTableIcons();
    this.changeDetectorRef.detectChanges();
  }

  handleEditClicked() {
    if (this.tableState === ModeIcons.add) {
      this.inlineTable.getRows().removeAt(0);
      this.inlineTable.getTableData();
      this.changeDetectorRef.detectChanges();
      const selected = this.inlineTable.inlineTableSelection.selected[0];
      this.editingId = selected.id;
      this.inlineTable.inlineTableSelection.clear();
    }

    this.tableState = ModeIcons.edit;
    if (this.inlineTable.inlineTableSelection.selected.length) {
      const selected = this.inlineTable.inlineTableSelection.selected[0];
      this.editingId = selected.id;
      this.inlineTable.inlineTableSelection.clear();
    }

    const row = this.getRowBeingEdited();
    row.enable();
    row.get('email')?.disable();
    this.updateTableIcons();
  }

  handleDeleteClicked() {
    const existingTableState = this.tableState;
    if (this.pageMode === ModeIcons.view) {
      this.accountsService
        .deleteUsers(this.inlineTable.inlineTableSelection.selected)
        .subscribe(data => {
          if (!data) {
            // do nothing - keep the selection as it is
            return;
          }

          // clear selection and reset state of accounts
          for (const user of this.inlineTable.inlineTableSelection.selected) {
            this.store.dispatch(new RemoveAccountUser(user));
          }
          this.removeFromGrid(this.inlineTable.inlineTableSelection.selected);
          this.inlineTable.inlineTableSelection.clear();
          this.resetStateAfterAPISuccess();
          this.tableState = existingTableState;
        });

      return;
    }

    // Delete the items from the store
    this.accountsService
      .deleteUserConfirmation(this.inlineTable.inlineTableSelection.selected)
      .afterClosed()
      .pipe(filter(data => !!data), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        for (const user of this.inlineTable.inlineTableSelection.selected) {
          this.store.dispatch(new RemoveAccountUser(user));
        }
        this.removeFromGrid(this.inlineTable.inlineTableSelection.selected);
        this.inlineTable.inlineTableSelection.clear();
        this.resetStateAfterAPISuccess();
        this.tableState = existingTableState;
      });
  }

  removeFromGrid(users: AccountUser[]) {
    for (const user of users) {
      const rows = this.inlineTable.getRows().getRawValue();

      const index = rows.findIndex(
        u => u.id === user.id || u.email === user.email
      );
      if (index !== -1) {
        this.inlineTable.getRows().removeAt(index);
      }
      this.inlineTable.getTableData();
    }
    this.changeDetectorRef.detectChanges();
  }

  resetStateAfterAPISuccess() {
    this.tableState = undefined;
    this.editingId = undefined;
    this.updateTableIcons();
    this.fullNameListenerSubscription?.unsubscribe();
    this.inlineTable.getTableData();
    this.changeDetectorRef.detectChanges();
  }

  checkMode() {
    this.commonService.isCheckForMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === ModeIcons.view) {
          this.pageMode = ModeIcons.view;
          this.handleCloseClicked();
        } else {
          this.pageMode = data != '' ? data : ModeIcons.add;
          // Add new row if not added at the top and cancel the previous action
          this.handleCloseClicked().then(() => {
            const row = this.inlineTable.getRows().at(0);
            if (!row || row?.disabled) {
              this.handleAddClicked();
            }
          });
        }
        this.updateTableIcons();
      });
  }

  private canSaveRow(): boolean {
    try {
      const row = this.getRowBeingEdited();
      if (!row) {
        return false;
      }
      return row.enabled && row.valid;
    } catch {
      return false;
    }
  }

  updateTableIcons(mode: string = this.pageMode) {
    const enableIcons = (enableIcons: string[]) => {
      let icons = structuredClone<SvgIconProp[]>(
        AccountUserManagementDefaultHeaderIcons
      );
      if (this.pageMode !== ModeIcons.view) {
        icons = icons.filter(icon => icon.name !== ActionIcons.add);
      }
      this.headerIcons = icons.map((icon: SvgIconProp) => {
        icon.isDisabled = !enableIcons.includes(icon.name);

        return icon;
      });
    };

    const iconsToEnable = [];
    const tableInEditOrAddMode =
      (!this.tableState && this.pageMode !== ModeIcons.view) ||
      this.tableState === ModeIcons.add ||
      this.tableState === ModeIcons.edit;

    if (
      mode === ModeIcons.add ||
      mode === ModeIcons.edit ||
      this.tableState === ModeIcons.add ||
      this.tableState === ModeIcons.edit
    ) {
      if (
        this.inlineTable.inlineTableSelection.selected.length ||
        (this.getRowBeingEdited() && this.canSaveRow())
      ) {
        iconsToEnable.push(ActionIcons.save);
      }
      iconsToEnable.push(ActionIcons.close);
    }

    if (this.inlineTable.inlineTableSelection.selected.length > 0) {
      iconsToEnable.push(ActionIcons.delete);
      if (this.getSelectionIds().length) {
        iconsToEnable.push(ActionIcons.mail);
      }
    }

    if (this.inlineTable.inlineTableSelection.selected.length === 1) {
      iconsToEnable.push(ActionIcons.edit);
    }

    if (
      !this.inlineTable.inlineTableSelection.selected.length &&
      !tableInEditOrAddMode
    ) {
      iconsToEnable.push(ActionIcons.add);
    }

    enableIcons(iconsToEnable);
  }

  initializeUsersGrid() {
    this.accountsService.isFetchAccountApi
      .pipe(
        combineLatestWith(this.accountsService.accountUserDataUpdated),
        switchMap(() => this.users$.pipe(take(1))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(users => {
        // remove the first empty row added by default
        if (this.inlineTable.getRows().length) {
          const indexToBeRemoved = this.inlineTable
            .getRows()
            .getRawValue()
            .findIndex(row => row.id === 0);
          this.inlineTable.getRows().removeAt(indexToBeRemoved);
          this.inlineTable.getTableData();
        }

        for (const user of users) {
          const rows = this.inlineTable.getRows().getRawValue();
          // User already exists
          const existingRow = rows.findIndex(u => u.email === user.email);
          if (existingRow !== -1) {
            this.inlineTable.getRows().at(existingRow).patchValue(user);
            continue;
          }

          this.inlineTable.addNewRow();
          const addedIndex = this.inlineTable.getRows().length - 1;
          this.inlineTable
            .getRows()
            .at(addedIndex)
            .patchValue({ ...user, isRowDisable: true, tempId: user.id });
          this.inlineTable.getRows().at(addedIndex).disable();
        }

        this.inlineTable.getTableData();
        this.changeDetectorRef.detectChanges();
      });
  }
}
